import { addDays, parseISO, startOfDay } from "date-fns";
import type { Trip } from "@/hooks/useTrips";

export interface TripInstallment {
  date: Date;
  amount: number;
  settled: boolean;
}

const roundCents = (n: number) => Math.round(n * 100) / 100;

/**
 * Split a trip's total_earnings into Turo-style weekly announced-payment installments.
 *
 * - Trips ≤ 7 days → single installment on trip_end.
 * - Longer trips  → N = ceil(days / 7) equal installments at trip_start + 7k
 *                   (k = 1..N-1), with the final installment on trip_end.
 * - Cancelled trips / missing earnings → returns [].
 */
export function getTripInstallments(trip: Trip, now: Date = new Date()): TripInstallment[] {
  if (!trip || trip.total_earnings == null) return [];
  if (trip.trip_status && trip.trip_status.toLowerCase().includes("cancel")) return [];

  const startStr = trip.trip_start;
  const endStr = trip.trip_end;
  if (!startStr && !endStr) return [];

  let start: Date | null = null;
  let end: Date | null = null;
  try { if (startStr) start = parseISO(startStr); } catch { /* ignore */ }
  try { if (endStr) end = parseISO(endStr); } catch { /* ignore */ }

  // Defensive fallback if one side is missing
  if (!start && end) start = end;
  if (!end && start) end = start;
  if (!start || !end) return [];

  const today = startOfDay(now);
  const total = trip.total_earnings;

  const msPerDay = 86_400_000;
  const rawDays = (end.getTime() - start.getTime()) / msPerDay;
  const days = Math.max(1, Math.round(rawDays));

  const make = (date: Date, amount: number): TripInstallment => ({
    date,
    amount,
    settled: startOfDay(date).getTime() <= today.getTime(),
  });

  if (days <= 7) {
    return [make(end, roundCents(total))];
  }

  const n = Math.ceil(days / 7);
  const per = roundCents(total / n);
  const installments: TripInstallment[] = [];
  let running = 0;
  for (let k = 1; k <= n - 1; k++) {
    installments.push(make(addDays(start, 7 * k), per));
    running += per;
  }
  // Final installment lands on trip_end and absorbs any rounding remainder
  const finalAmount = roundCents(total - running);
  installments.push(make(end, finalAmount));
  return installments;
}
